// This is the new component rendered for Filter UI Rework:
import React, { useMemo } from 'react';
import Icon from '@components/atoms/icon/icon';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import Switch from '@components/catalog/switch/switch';
import { CatalogContext } from '@context/catalog.context';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import MySizesBadges from '@components/catalog/my-sizes/mysizes-filter-section/mysizes-badges/mysizes-badges';
import AuthenticationGuard from '@components/atoms/authentication/authentication-guard/authentication-guard';
import styles from './mysizes-filter-section.module.scss';

interface MySizesFilterSectionProps {
  showNoResult: boolean;
  showAddMySizes: boolean;
  showCurrentMySizes: boolean;
  handleSwitchChange: (checked: boolean) => void;
  handleOpenEditDialog: () => void;
  handleOpenConfigurationDialog: () => void;
}

const MySizesFilterSection: React.FC<MySizesFilterSectionProps> = ({
  showAddMySizes,
  showCurrentMySizes,
  showNoResult,
  handleSwitchChange,
  handleOpenEditDialog,
  handleOpenConfigurationDialog,
}) => {
  const { t } = useTranslation();
  const areMySizesEnabled = useContextSelector(CatalogContext, (v) => v.areMySizesEnabled);

  const subtitle = useMemo(() => {
    if (showAddMySizes) {
      return 'MYSIZES.FILTER_NOT_SET.SUBTITLE';
    }
    if (showNoResult) {
      return 'MYSIZES.FILTER_SET.NORESULT';
    }
    return 'MYSIZES.FILTER_SET.SUBTITLE';
  }, [showAddMySizes, showNoResult]);

  return (
    <div className={styles.mysizesFilterSection__container}>
      <div className={styles.mysizesFilterSection__header}>
        <div>
          <div className={styles.mysizesFilterSection__title}>
            <Icon
              name="my-sizes-16"
              className={styles.mysizesFilterSection__icon}
              ariaHidden
            />
            <h3
              id={'my-sizes-title'}
              className="vc-text-m"
            >
              {t('MYSIZES.SECTION.TITLE')}
            </h3>
          </div>
          <div
            className={clsx(styles.mysizesFilterSection__subtitle, 'vc-text-s')}
            data-cy="my-sizes-filter-subtitle"
          >
            {t(subtitle)}
          </div>
        </div>
        {(showNoResult || showCurrentMySizes) && (
          <div>
            <Switch
              dataCyElementLocator="my-sizes-switch"
              checked={areMySizesEnabled}
              onChange={handleSwitchChange}
              disabled={showNoResult}
              ariaLabelledby={'my-sizes-title'}
            />
          </div>
        )}
      </div>
      {showCurrentMySizes && (
        <MySizesBadges
          handleOpenEditDialog={handleOpenEditDialog}
          editBtnClassName={clsx(styles.mysizesFilterSection__btn, styles.mysizesFilterSection__btn__edit)}
        />
      )}
      {showAddMySizes && (
        <AuthenticationGuard>
          <Button
            size="medium"
            className={clsx(styles.mysizesFilterSection__btn, styles.mysizesFilterSection__btn__add)}
            onClick={handleOpenConfigurationDialog}
            data-cy="my-sizes-add-button"
          >
            {t('PERSO.FILTER_NOT_SET.CTA')}
          </Button>
        </AuthenticationGuard>
      )}
    </div>
  );
};

export default MySizesFilterSection;
